import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDownTrayIcon, ArrowUturnLeftIcon, ChatBubbleLeftRightIcon, ClipboardDocumentListIcon, ClockIcon, DocumentPlusIcon, DocumentTextIcon, RectangleStackIcon } from "@heroicons/react/24/outline";
import { toast } from "../../../utils/toast";
import { hasAnyRole } from "../../../utils/auth";
import { ROLES } from "../../../auth/roles";
import { Button, ConfirmModal, EmptyState, InfoBox, Loading, Modal, RowAction, Table, Tabs, Tag, Textarea } from "../../ui";
import { errorMessageOf, scholarshipsApi } from "../../../api/scholarships";
import ApplicationContents from "./ApplicationContents";
import EligibilityFlag from "./EligibilityFlag";
import ReviewHistory, { COMMENT_MAX, reviewHistoryKey } from "./ReviewHistory";
import MessageThread from "../loans/MessageThread";
import { NOMINEE_STATES, StaffDocumentRequests } from "../DocumentRequests";
import { beneficiaryStatusOf } from "../cooperation/model";
import { loanStatusOf, rejectedByLabel } from "../loans/model";
import { formatMoney, loanSummary } from "../funding";
import { formatKigaliDate } from "../model";
import { REVIEW_KEY, adminApplicationKey, applicationStateOf, evaluationStateOf, formatDateTime, formatLocalDateTime, formatScore, fullName, openDocument, useAdminApplication } from "./model";

const TABS = [
  { value: "application", label: "Application", icon: <DocumentTextIcon className="h-4 w-4" /> },
  { value: "evaluations", label: "Evaluations", icon: <ClipboardDocumentListIcon className="h-4 w-4" /> },
  { value: "history", label: "History", icon: <ClockIcon className="h-4 w-4" /> },
];
// Every round of marks and comments: the Scholarship Admin, the DG and the Minister only (each read is audited)
const REVIEW_TAB = { value: "review", label: "Review history", icon: <RectangleStackIcon className="h-4 w-4" /> };
// Once the student accepts a government loan, HEC reads their messages with BRD
const MESSAGES_TAB = { value: "messages", label: "Messages", icon: <ChatBubbleLeftRightIcon className="h-4 w-4" /> };
// Once the nominee is with the sponsor: what the owner asked for and the student sent
const REQUESTS_TAB = { value: "requests", label: "Requests to the student", icon: <DocumentPlusIcon className="h-4 w-4" /> };

const ACTIONS = {
  CARRIED_OVER: "Carried over from the abroad scholarship records",
  STARTED: "Started the application",
  SUBMITTED: "Submitted",
  WITHDRAWN: "Withdrew the application",
  EXPIRED: "Expired",
  DOCUMENT_UPLOADED: "Uploaded a document",
  DOCUMENT_REMOVED: "Removed a document",
  DOCUMENT_LINK_ISSUED: "Opened a document",
  REVIEW_STARTED: "Review started",
  REVIEW_COMPLETED: "Review completed",
  EVALUATOR_ADDED: "Evaluator added",
  EVALUATOR_REMOVED: "Evaluator removed",
  CONFLICT_DECLARED: "Removed for a conflict of interest",
  CONFLICT_WITHDRAWN: "Put back after a conflict was withdrawn",
  NO_CONFLICT_DECLARED: "Declared no conflict of interest",
  EVALUATION_SUBMITTED: "Submitted their marks",
  EVALUATION_REOPENED: "Sent marks back for re-marking",
  OUTCOME_PUBLISHED: "Outcome published",
  OUTCOME_SHOWN: "Outcome shown to the student",
  DRAFT_REVIVED: "Draft reopened with the call",
  // A sponsored scholarship's nomination
  SENT_TO_SPONSOR: "Sent to the partner",
  GRANTED_BY_SPONSOR: "Granted by the partner",
  REFUSED_BY_SPONSOR: "Not granted by the partner",
  RESERVE_NOMINATED: "Sent to the partner from the reserve list",
  RESERVE_RELEASED: "Not selected: nominations closed",
  // The owner's follow-up of an awardee
  RECEIVED_BY_PARTNER: "Received by the partner",
  RECEIVED_BY_UNIVERSITY: "Admitted by a university",
  ENROLLED: "Enrolled",
  DROPPED: "Dropped",
  BENEFICIARY_STATUS_CORRECTED: "Follow-up corrected",
  // Cooperation scholarships decided by their partner before 14 October 2026
  NOT_SHORTLISTED: "Not shortlisted",
  GRANTED: "Granted",
  REJECTED: "Rejected",
  STATUS_CORRECTED: "Status corrected",
  // Government loans
  HEC_DECIDED: "HEC's award list decided",
  SENT_TO_DG: "Sent to the DG",
  DG_APPROVED: "Approved by the DG, sent to the Minister",
  REJECTED_BY_DG: "Rejected by the DG",
  RETURNED_TO_HEC: "Returned to HEC",
  RESENT_TO_DG: "Sent back to the DG",
  SENT_TO_MINISTER: "Sent to the Minister",
  RESENT_TO_MINISTER: "Sent back to the Minister",
  PROMOTED: "Promoted from the reserve list",
  MINISTER_APPROVED: "Approved by the Minister",
  REJECTED_BY_MINISTER: "Rejected by the Minister",
  RETURNED_TO_DG: "Returned to the DG",
  REJECTED_BY_HEC: "Rejected by HEC",
  LOAN_AMOUNTS_CHANGED: "Loan amounts changed",
  LOAN_ACCEPTED: "Accepted the loan",
  LOAN_DECLINED: "Declined the loan",
  TRAVEL_DETAILS_SENT: "Sent the travel details",
  VISA_UPLOADED: "Uploaded the visa",
  VISA_REPLACED: "Replaced the visa",
  TICKET_ISSUED: "Uploaded the ticket",
  TICKET_REPLACED: "Replaced the ticket",
  TUITION_PAID: "Paid the tuition",
  ALLOWANCE_SENT: "Sent an allowance to the embassy",
  PAYMENT_REMOVED: "Removed a payment",
};

// A code without a label still reads as words ("SOME_NEW_STEP" → "Some new step"), and is reported
const actionLabel = (code) => {
  if (ACTIONS[code]) return ACTIONS[code];
  console.warn(`No label for the history action ${code}`);
  const words = String(code ?? "").toLowerCase().replaceAll("_", " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

function History({ applicationId, basePath }) {
  const { data = [], isPending } = useQuery({
    queryKey: [...REVIEW_KEY, "application-history", applicationId, basePath],
    // The API lists the oldest first; newest first reads better here
    queryFn: async () => [...(await scholarshipsApi.get(`${basePath}/applications/${applicationId}/history`)).data].reverse(),
  });
  if (isPending) return <Loading label="Loading the history…" />;
  return (
    <ol className="relative flex flex-col gap-4 border-l border-slate-200 pl-6">
      {data.map((entry, i) => (
        <li key={`${entry.occurredAt}-${i}`} className="relative">
          <span className="absolute -left-[1.85rem] top-1.5 h-3 w-3 rounded-full border-2 border-white bg-brand" />
          <p className="text-sm font-semibold text-slate-800">
            {actionLabel(entry.action)}
          </p>
          <p className="text-xs text-slate-500">
            {formatDateTime(entry.occurredAt)}
            {entry.actor?.name && ` · ${entry.actor.name}`}
          </p>
          {entry.reason && <p className="mt-1 text-sm italic text-slate-600">“{entry.reason}”</p>}
        </li>
      ))}
    </ol>
  );
}

// An awardee's follow-up by the scholarship's owner, and the reason for a drop or a correction
function FollowUpStatus({ beneficiary }) {
  const status = beneficiaryStatusOf(beneficiary.status);
  return (
    <InfoBox>
      <span className="flex flex-wrap items-center gap-2">
        <span className="font-medium">Follow-up:</span>
        <Tag color={status.color}>{status.label}</Tag>
        {beneficiary.updatedAt && <span className="text-slate-500">since {formatDateTime(beneficiary.updatedAt)}</span>}
      </span>
      {beneficiary.reason && <span className="mt-1 block italic">“{beneficiary.reason}”</span>}
    </InfoBox>
  );
}

// Where a government loan stands, its approved amounts, and the latest reason or note
const TRAVEL_LABELS = [
  ["departureAirport", "From"],
  ["arrivalAirport", "To"],
  ["preferredTravelDate", "Preferred date"],
  ["passportNumber", "Passport"],
  ["passportExpiry", "Passport expires"],
];

// Where the loan stands; once approved, the letter, the student's answer and their travel details.
// A sponsored scholarship's nominee has the same record without amounts: only where it stands.
function LoanStatus({ applicationId, loan }) {
  const isLoan = Boolean(loan.approvedFunding);
  const status = loanStatusOf(loan.status, isLoan);
  const { letter, response, travel } = loan;
  return (
    <InfoBox>
      <span className="flex flex-wrap items-center gap-2">
        <Tag color={status.color}>{status.label}</Tag>
        {isLoan && loan.status === "REJECTED" && <span>{rejectedByLabel(loan.rejectedBy)}</span>}
        {isLoan && <span>Loan {loanSummary(loan.approvedFunding)}</span>}
        <span className="text-slate-500">since {formatDateTime(loan.updatedAt)}</span>
        {letter && <RowAction Icon={ArrowDownTrayIcon} label={`Letter ${letter.reference}`} variant="ghost" onClick={() => openDocument(`staff/applications/${applicationId}/loan/letter/download-url`)} />}
      </span>
      {(loan.reason || loan.note) && <span className="mt-1 block italic">“{loan.reason ?? loan.note}”</span>}
      {letter && (
        <span className="mt-1 block">
          {response ? `Student ${response.accepted ? "accepted" : "declined"} ${formatDateTime(response.at)}` : "Waiting for the student's answer"}
          {response?.reason && <span className="italic"> · “{response.reason}”</span>}
        </span>
      )}
      {travel && (
        <span className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="font-medium">{travel.submittedAt ? `Travel details sent ${formatDateTime(travel.submittedAt)}` : "Travel details not sent yet"}</span>
          {TRAVEL_LABELS.filter(([key]) => travel[key]).map(([key, label]) => (
            <span key={key}>
              {label}: {key === "preferredTravelDate" || key === "passportExpiry" ? formatKigaliDate(travel[key]) : travel[key]}
            </span>
          ))}
          {travel.visa && <RowAction Icon={ArrowDownTrayIcon} label="Visa" variant="ghost" onClick={() => openDocument(`staff/applications/${applicationId}/travel/visa/download-url`)} />}
        </span>
      )}
      {travel?.ticket && (
        <span className="mt-1 flex flex-wrap items-center gap-2">
          Ticket {travel.ticket.airline} {travel.ticket.flightNumber}, departs {formatLocalDateTime(travel.ticket.departureAt)}
          <RowAction Icon={ArrowDownTrayIcon} label="Ticket" variant="ghost" onClick={() => openDocument(`staff/applications/${applicationId}/travel/ticket/download-url`)} />
        </span>
      )}
      {loan.payments?.length > 0 && (
        <span className="mt-1 block">
          BRD paid{" "}
          {loan.payments
            .map((p) => `${p.kind === "TUITION" ? "tuition" : "allowance"} ${formatMoney(p.amount, p.currency)} (${formatKigaliDate(p.paidOn)})`)
            .join(", ")}
        </span>
      )}
    </InfoBox>
  );
}

/**
 * One application in full: for HEC staff (`basePath` "admin"), or ("staff") for the scholarship's
 * owner (its awardees only), the DG or the Minister, who read it but cannot reopen marks.
 * `programmes`: the scholarship's offered programmes, whose rubric heads the marks. `sponsored`: a
 * cooperation scholarship, whose nominees answer the owner's requests for more.
 */
export default function ApplicationDialog({ applicationId, programmes = [], onClose, basePath = "admin", sponsored = true }) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState("application");
  const [reopening, setReopening] = useState(null); // an evaluation waiting for a revert comment
  const [reason, setReason] = useState("");
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const { data, isPending, isError, refetch } = useAdminApplication(applicationId, basePath);
  const application = data?.data;
  const rubric = programmes.find((p) => p.id === application?.programme?.id)?.rubric ?? [];
  // Partners never see HEC's evaluations (the API sends them none)
  // Owners never read comments; the API refuses them too
  const readsComments = hasAnyRole(ROLES.ADMIN, ROLES.DG, ROLES.MINISTER);
  const tabs = [
    ...TABS.filter((t) => t.value !== "evaluations" || !hasAnyRole(ROLES.SCHOLARSHIP_OWNER)),
    ...(readsComments ? [REVIEW_TAB] : []),
    ...(application?.loan?.response?.accepted ? [MESSAGES_TAB] : []),
    ...(sponsored && NOMINEE_STATES.includes(application?.state) ? [REQUESTS_TAB] : []),
  ];
  // Marks can be reopened only while HEC is still reviewing
  const canReopen = basePath === "admin" && hasAnyRole(ROLES.ADMIN) && ["SUBMITTED", "IN_REVIEW", "REVIEWED"].includes(application?.state);
  const reasonError = tried && !reason.trim() ? "Write a comment for the evaluator." : undefined;
  const askToSendBack = (evaluation) => {
    setReason("");
    setTried(false);
    setReopening(evaluation);
  };

  const reopen = async () => {
    setTried(true);
    if (!reason.trim()) return;
    setBusy(true);
    try {
      const result = await scholarshipsApi.post(
        `admin/applications/${applicationId}/evaluations/${reopening.evaluator.id}/reopen`,
        { reason: reason.trim() },
        { ifMatch: data.etag ?? `"${application.version}"` }
      );
      queryClient.setQueryData([...adminApplicationKey(applicationId), basePath], result);
      queryClient.invalidateQueries({ queryKey: REVIEW_KEY });
      queryClient.invalidateQueries({ queryKey: reviewHistoryKey("admin", applicationId) });
      toast.success(`Sent back to ${reopening.evaluator.name}, who will see your comment`);
      setReopening(null);
      setReason("");
    } catch (error) {
      console.error(`Failed to reopen an evaluation of application ${applicationId}:`, error);
      toast.error(errorMessageOf(error, "The marks were not sent back. Please try again."));
      refetch();
    } finally {
      setBusy(false);
    }
  };

  const markOf = (evaluation, criterionId) => evaluation.marks.find((m) => m.criterionId === criterionId)?.marks;
  const evaluationColumns = [
    { key: "evaluator", label: "Evaluator", accessor: (e) => e.evaluator.name, render: (e) => <span className="font-medium text-slate-800">{e.evaluator.name}</span> },
    { key: "state", label: "State", accessor: (e) => evaluationStateOf(e.state).label, render: (e) => <Tag color={evaluationStateOf(e.state).color}>{evaluationStateOf(e.state).label}</Tag> },
    ...rubric.map((c) => ({ key: c.id, label: `${c.name} (${c.weight}%)`, accessor: (e) => markOf(e, c.id) ?? "—" })),
    { key: "weightedTotal", label: "Total", accessor: (e) => formatScore(e.weightedTotal), sortValue: (e) => e.weightedTotal },
    { key: "submittedAt", label: "Submitted", accessor: (e) => formatDateTime(e.submittedAt) },
    ...(canReopen
      ? [{ key: "reopen", label: "", exportable: false, render: (e) => e.state === "SUBMITTED" && <RowAction Icon={ArrowUturnLeftIcon} label={`Send ${e.evaluator.name}'s marks back`} variant="ghost" onClick={() => askToSendBack(e)} /> }]
      : []),
  ];

  const state = applicationStateOf(application?.state);

  return (
    <Modal
      open={Boolean(applicationId)}
      onClose={onClose}
      size="full"
      title={
        <span className="flex flex-wrap items-center gap-3">
          {application ? fullName(application.applicant) : "Application"}
          {application && <Tag color={state.color}>{state.label}</Tag>}
        </span>
      }
      description={application && `${application.applicant.email}${application.applicant.phoneNumber ? ` · ${application.applicant.phoneNumber}` : ""} · submitted ${formatDateTime(application.submittedAt)}`}
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      {isPending ? (
        <Loading label="Loading the application…" />
      ) : isError ? (
        <EmptyState title="Could not load this application" onRefresh={refetch} />
      ) : (
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Tabs tabs={tabs} value={tab} onChange={setTab} />
            <p className="text-sm text-slate-600">
              Mean score <span className="text-lg font-bold text-slate-900">{formatScore(application.meanScore)}</span>
            </p>
          </div>
          <EligibilityFlag eligibility={application.eligibility} />
          {application.beneficiary && <FollowUpStatus beneficiary={application.beneficiary} />}
          {application.loan && <LoanStatus applicationId={applicationId} loan={application.loan} />}
          {tab === "application" && (
            <ApplicationContents
              application={application}
              documentPath={(id) => `${basePath}/applications/${applicationId}/documents/${id}/download-url`}
              attachmentPath={basePath === "admin" ? (id) => `admin/applications/${applicationId}/attachments/${id}/download-url` : null}
            />
          )}
          {tab === "evaluations" && (
            <div className="flex flex-col gap-3">
              <Table columns={evaluationColumns} data={application.evaluations} searchable={false} exportable={false} emptyTitle="No evaluators assigned" />
              {application.evaluations
                .filter((e) => e.conflictDeclared)
                .map((e) => (
                  <p key={e.evaluator.id} className="text-sm text-slate-600">
                    <span className="font-medium">{e.evaluator.name}</span> removed this applicant for a conflict of interest{e.conflictNote ? `: “${e.conflictNote}”` : "."}
                  </p>
                ))}
            </div>
          )}
          {tab === "review" && readsComments && (
            <ReviewHistory applicationId={applicationId} path="admin" canCorrect={(comment) => comment.kind === "REVERT" && hasAnyRole(ROLES.ADMIN)} />
          )}
          {tab === "history" && <History applicationId={applicationId} basePath={basePath} />}
          {tab === "messages" && <MessageThread path={`staff/applications/${applicationId}/messages`} side={null} />}
          {tab === "requests" && <StaffDocumentRequests applicationId={applicationId} />}
        </div>
      )}

      <ConfirmModal
        open={Boolean(reopening)}
        onClose={() => setReopening(null)}
        onConfirm={reopen}
        loading={busy}
        title={`Send ${reopening?.evaluator.name}'s marks back?`}
        description="They mark this application again in a new round. Their earlier marks and comments are kept as they are, and the application goes back into review."
        confirmLabel="Send back"
      >
        <Textarea
          label="Comment for the evaluator"
          required
          rows={3}
          maxLength={COMMENT_MAX}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Tell the evaluator what to look at again"
          info={`${reason.length} of ${COMMENT_MAX} characters. The evaluator sees this above their marking form. It is kept permanently; you can add a correction later but not edit it.`}
          error={reasonError}
        />
      </ConfirmModal>
    </Modal>
  );
}
